import { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Unstable_Grid2 as Grid,
  TextField,
  Alert,
  MenuItem,
  Button,
  Stack,
  Divider,
  CircularProgress,
  Switch,
  AppBar,
  Toolbar,
  Typography,
  Chip,
  Paper,
} from "@mui/material";

import SelectChipField from "../../../../components/SelectChipField";
import TaskGrid from "../../../../components/TaskGrid";
import TaskHeader from "../../../../components/TaskHeader";
import StartDateDialog from "../../../../components/StartDateDialog";
import TypeToggleButton from "../../../../components/TypeToggleButton";

import EditIcon from "@mui/icons-material/Edit";
import AssignmentIcon from "@mui/icons-material/Assignment";

import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { useOrganization } from "..";
import { useAuth } from "../../../../contexts/AuthContext";
import { useDialog } from "../../../../contexts/DialogContext";

import { fetchData } from "../../../../fetchData";

type FormValues = {
  info: string;
  // type: string;
  adiantamento: string;
  sindPatronal: string;
  sindClasse: string;
  fopagDataPGTO: string;
  fopagDataADTO: string;
  fopagDataTypePGTO: string;
  fopagDataTypeADTO: string;
  funcCLT: string;
  observations: string;
};

type DpTask = {
  on: boolean;
  name: string;
  date: string;
  responsible: string | number;
  schedule?: {
    frequency: "daily" | "weekly" | "monthly";
    dayOfWeek: number[];
    monthsInterval?: number;
    startDate?: string;
  };
};

function scheduleStartDateToISO(
  startDate: string | undefined,
): string | undefined {
  if (!startDate) return undefined;
  if (typeof startDate !== "string") return undefined;
  if (startDate.includes("T")) return startDate;

  const parts = startDate.split("/");
  if (parts.length !== 3) return startDate;

  const [d, m, y] = parts;
  const date = new Date(Number(y), Number(m) - 1, Number(d));

  return isNaN(date.getTime()) ? startDate : date.toISOString();
}

const OrganizationDpTab = () => {
  const auth = useAuth();
  const snackbar = useSnackbar();
  const { data: organization, set } = useOrganization();
  const dialog = useDialog();
  const [progress, setProgress] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tasks, setTasks] = useState<DpTask[]>([]);
  const [tasksBackup, setTasksBackup] = useState<DpTask[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [taskErrors, setTaskErrors] = useState<{ responsible: boolean }[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<any[]>([]);
  const [masterSwitch, setMasterSwitch] = useState(false);
  const [isObservationsFocused, setIsObservationsFocused] = useState(false);
  const [startDateDialogOpen, setStartDateDialogOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<any>(null);
  const fieldRefs = useRef<(HTMLDivElement | null)[]>([]);
  const {
    register,
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      info: organization.dp?.info || "",
      // type: organization.dp?.type || "",
      adiantamento: organization.dp?.adiantamento || "",
      sindPatronal: organization.dp?.sindPatronal || "",
      sindClasse: organization.dp?.sindClasse || "",
      fopagDataPGTO: organization.dp?.fopagDataPGTO || "",
      fopagDataADTO: organization.dp?.fopagDataADTO || "",
      fopagDataTypePGTO: organization.dp?.fopagDataTypePGTO || "util",
      fopagDataTypeADTO: organization.dp?.fopagDataTypeADTO || "util",
      funcCLT: organization.dp?.funcCLT || "",
      observations: organization.dp?.observations || "",
    },
  });

  async function loadData() {
    setProgress(true);
    try {
      // Carrega usuários primeiro
      const usersResult = await fetchData("/runrunit/users", {
        method: "GET",
      });
      setUsers(usersResult);

      // Busca os tipos de tarefa DP
      const result = await fetchData(`/task-types?autoUpload=true`, {
        method: "GET",
      });
      const filtered = (result?.rows || []).filter(
        (t: any) => t.tribe && t.tribe.includes("DP"),
      );
      setFilteredTasks(filtered);

      // Busca os autoTasks do cliente
      const client = await fetchData(
        `/organizations/${organization.reference}/ref`,
        { method: "GET" },
      );
      const autoTasks = client?.autoTasks || [];

      // Faz o merge: sempre exibe as tarefas do tipo DP, preenchendo com autoTasks se existir
      const mapped = filtered
        .filter((t: any) => {
          if (t.name && (t.name === "DCTFWeb" || t.name === "DIRF")) {
            const folhaPagamentoStatus = client.dp?.info;
            return folhaPagamentoStatus !== "Não Possui";
          }
          return true;
        })
        .map((t: any) => {
          const found = autoTasks.find(
            (a: any) => a.taskTypeID === t.runrunit_id,
          );

          return {
            on: found ? found.active : false,
            name: t.name,
            date: found ? String(found.uploadDay).padStart(2, "0") : "01",
            responsible: found ? found.user : "",
            schedule: found?.schedule
              ? {
                  frequency: found.schedule.frequency || "monthly",
                  dayOfWeek: found.schedule.dayOfWeek || [],
                  monthsInterval: found.schedule.monthsInterval || 1,
                  startDate: found.schedule.startDate
                    ? new Date(found.schedule.startDate).toLocaleDateString(
                        "pt-BR",
                      )
                    : undefined,
                }
              : {
                  frequency: "monthly",
                  dayOfWeek: [],
                  monthsInterval: 1,
                },
          };
        });
      setTasks(mapped);
      setTasksBackup(mapped);

      // Reset form com dados atualizados
      reset({
        info: client.dp?.info || "",
        // type: client.dp?.type || "",
        adiantamento: client.dp?.adiantamento || "",
        sindPatronal: client.dp?.sindPatronal || "",
        sindClasse: client.dp?.sindClasse || "",
        fopagDataPGTO: client.dp?.fopagDataPGTO || "",
        fopagDataADTO: client.dp?.fopagDataADTO || "",
        fopagDataTypePGTO: client.dp?.fopagDataTypePGTO || "util",
        fopagDataTypeADTO: client.dp?.fopagDataTypeADTO || "util",
        funcCLT: client.dp?.funcCLT || "",
        observations: client.dp?.observations || "",
      });
    } catch (e) {
      console.error("Erro no loadData DP:", e);
      setTasks([]);
      setTasksBackup([]);
      setUsers([]);
    }
    setProgress(false);
  }

  const handleTaskToggleAttempt = (taskName: string, checked: boolean) => {
    const folhaStatus = getValues("info");

    if (folhaStatus === "Não Possui" && taskName !== "DET - Monitoramento") {
      snackbar?.open(
        "Não é possível ativar tarefas quando a Folha de Pagamento está marcada como 'Não Possui'",
        "warning",
      );
      return;
    }

    if (
      folhaStatus === "Com alteração" &&
      taskName === "Folha de Pagamento Sem Alteração"
    ) {
      snackbar?.open(
        "Não é possível ativar 'Folha de Pagamento Sem Alteração' quando a Folha de Pagamento está marcada como 'Com alteração'",
        "warning",
      );
      return;
    }

    if (
      folhaStatus === "Sem alteração" &&
      taskName === "Folha de Pagamento Com Alteração"
    ) {
      snackbar?.open(
        "Não é possível ativar 'Folha de Pagamento Com Alteração' quando a Folha de Pagamento está marcada como 'Sem alteração'",
        "warning",
      );
      return;
    }
  };

  const handleFolhaPagamentoChange = (newValue: string) => {
    if (!isEditing) return;

    const newTasks = [...tasks];

    if (newValue === "Não Possui") {
      newTasks.forEach((task) => {
        if (task.name !== "DET - Monitoramento") {
          task.on = false;
        }
      });
    } else if (newValue === "Com alteração") {
      newTasks.forEach((task) => {
        if (task.name === "Folha de Pagamento Sem Alteração") {
          task.on = false;
        }
      });
    } else if (newValue === "Sem alteração") {
      newTasks.forEach((task) => {
        if (task.name === "Folha de Pagamento Com Alteração") {
          task.on = false;
        }
      });
    }

    setTasks(newTasks);
  };

  const handleMasterSwitchChange = (checked: boolean) => {
    const currentTasks = [...tasks];
    const updatedTasks = currentTasks.map((task) => {
      const folhaPagamentoStatus = getValues("info");

      if (
        folhaPagamentoStatus === "Não Possui" &&
        task.name !== "DET - Monitoramento"
      ) {
        return { ...task, on: false };
      }

      if (
        folhaPagamentoStatus === "Com alteração" &&
        task.name === "Folha de Pagamento Sem Alteração"
      ) {
        return { ...task, on: false };
      }

      if (
        folhaPagamentoStatus === "Sem alteração" &&
        task.name === "Folha de Pagamento Com Alteração"
      ) {
        return { ...task, on: false };
      }

      return { ...task, on: checked };
    });

    setTasks(updatedTasks);
    setMasterSwitch(checked);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [organization.reference]);

  const onInvalid = () => {
    snackbar?.open("Preencha os campos obrigatórios.", "error");
  };

  async function onSubmit(data: any) {
    const taskValidationErrors = tasks.map((task) => ({
      responsible: task.on && !task.responsible,
    }));
    setTaskErrors(taskValidationErrors);
    const hasError = taskValidationErrors.some((err) => err.responsible);
    if (hasError) {
      // Não exibe snackbar, só deixa os campos vermelhos
      return;
    }

    // Verificar se há tarefas ativas com intervalo >= 2 meses
    const tasksWithInterval = tasks.filter(
      (task) =>
        task.on &&
        task.schedule?.frequency === "monthly" &&
        (task.schedule?.monthsInterval || 1) >= 2,
    );

    if (tasksWithInterval.length > 0) {
      setPendingFormData(data);
      setStartDateDialogOpen(true);
      return;
    }

    await saveWithConflictCheck(data);
  }

  async function saveWithConflictCheck(data: any, forceUpdate = false) {
    setSaving(true);
    // Monta o array autoTasks para salvar no backend
    // Filtra apenas as tasks que estão visíveis (não foram escondidas pelo filtro)
    const visibleTasks = tasks.filter((task) => {
      // Encontrar o taskType correspondente pelo nome
      const taskType = filteredTasks.find((ft) => ft.name === task.name);

      if (taskType?.name === "DCTFWeb" || taskType?.name === "DIRF") {
        const folhaPagamentoStatus = organization.dp?.info;
        return folhaPagamentoStatus !== "Não Possui";
      }
      return true;
    });

    const autoTasks = visibleTasks.map((task) => {
      const taskType = filteredTasks.find((ft) => ft.name === task.name);

      const schedule = task.schedule
        ? {
            ...task.schedule,
            startDate: scheduleStartDateToISO(task.schedule.startDate),
          }
        : undefined;

      const autoTask = {
        taskTypeID: (taskType && taskType.runrunit_id) || null,
        taskName: (taskType && taskType.name) || "",
        active: task.on,
        uploadDay: Number(task.date) || 1,
        user: task.responsible,
        tribe: (taskType && taskType.tribe) || [],
        schedule,
      };

      return autoTask;
    });
    const payload = { ...organization, dp: data, autoTasks, forceUpdate };

    try {
      await fetchData(`/organizations/${organization._id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      set(payload);
      setIsEditing(false);
      setTasksBackup(tasks);
      snackbar?.open("Informações de DP atualizadas com sucesso!", "success");
      await loadData();
    } catch (error: any) {
      // Verificar se é um erro de conflito
      const errorMessage = error?.message || "";

      if (errorMessage.startsWith("TASK_CONFLICTS:")) {
        const conflictsJson = errorMessage.replace("TASK_CONFLICTS:", "");
        try {
          const conflicts = JSON.parse(conflictsJson);

          // Usar o dialog existente
          dialog?.open(
            "Conflito de Tasks",
            <Box sx={{ p: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                As seguintes tasks não podem estar ativas ao mesmo tempo:
              </Alert>

              {conflicts.map((conflict: any, index: number) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: 1,
                    borderColor: "grey.300",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Conflito {index + 1}:
                  </Typography>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip label="Nova Task" color="primary" size="small" />
                      <Typography variant="body2">
                        {conflict.newTask.name} (Dia{" "}
                        {conflict.newTask.uploadDay} - {conflict.newTask.user})
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label="Task Conflitante"
                        color="error"
                        size="small"
                      />
                      <Typography variant="body2">
                        {conflict.conflictingTask.name} (Dia{" "}
                        {conflict.conflictingTask.uploadDay} -{" "}
                        {conflict.conflictingTask.user})
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}

              <Alert severity="info" sx={{ mt: 2 }}>
                Se você prosseguir, as tasks conflitantes serão automaticamente
                desativadas.
              </Alert>

              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button onClick={dialog?.close} color="inherit">
                  Cancelar
                </Button>
                <Button
                  onClick={() => {
                    dialog?.close();
                    saveWithConflictCheck(data, true);
                  }}
                  variant="contained"
                  color="warning"
                >
                  Prosseguir e Desativar Conflitantes
                </Button>
              </Stack>
            </Box>,
          );

          return; // Não mostrar erro padrão
        } catch (e) {
          console.error("Erro ao parsear conflitos:", e);
        }
      }

      snackbar?.open(
        "Ops! No momento não foi possível realizar a ação.",
        "error",
      );
    }
    setSaving(false);
  }

  const handleStartDateDialogClose = () => {
    setStartDateDialogOpen(false);
    setPendingFormData(null);
  };

  const handleStartDateDialogConfirm = async (tasksWithStartDate: any[]) => {
    // Atualizar as tarefas com as datas de início configuradas
    const updatedTasks = tasks.map((task) => {
      const taskType = filteredTasks.find((ft) => ft.name === task.name);
      const taskTypeID = (taskType && taskType.runrunit_id) || 0;
      const updatedTask = tasksWithStartDate.find(
        (t) => t.taskTypeID === taskTypeID,
      );
      if (updatedTask && updatedTask.schedule) {
        return {
          ...task,
          schedule: updatedTask.schedule,
        };
      }
      return task;
    });

    setTasks(updatedTasks);
    setStartDateDialogOpen(false);

    // Agora salvar com os dados pendentes
    if (pendingFormData) {
      await saveWithConflictCheck(pendingFormData);
      setPendingFormData(null);
    }
  };

  // Converter tarefas para o formato esperado pelo dialog
  const tasksForDialog = tasks.map((task) => {
    const taskType = filteredTasks.find((ft) => ft.name === task.name);
    return {
      taskTypeID: (taskType && taskType.runrunit_id) || 0,
      taskName: (taskType && taskType.name) || task.name,
      active: task.on,
      uploadDay: Number(task.date),
      user: task.responsible,
      tribe: (taskType && taskType.tribe) || [],
      schedule: task.schedule,
    };
  });

  return (
    <Box sx={{ p: 2, position: "relative", pb: 4 }}>
      <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <Grid container spacing={2}>
          <Grid xs={12} lg={4}>
            <Controller
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Folha de Pagamento"
                  size="small"
                  margin="dense"
                  disabled={!isEditing}
                  fullWidth
                  variant="outlined"
                  error={!!errors.info}
                  helperText={errors.info?.message?.toString()}
                  onChange={(e) => {
                    field.onChange(e);
                    handleFolhaPagamentoChange(e.target.value);
                  }}
                >
                  {["Com alteração", "Sem alteração", "Não Possui"].map(
                    (label: string) => (
                      <MenuItem key={label} value={label}>
                        {label}
                      </MenuItem>
                    ),
                  )}
                </TextField>
              )}
              control={control}
              name="info"
              rules={{ required: "Selecione a Folha de Pagamento" }}
            />
          </Grid>
          <Grid xs={12} lg={4}>
            <Controller
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Adiantamento"
                  size="small"
                  margin="dense"
                  disabled={!isEditing}
                  fullWidth
                  variant="outlined"
                >
                  {["Sim", "Não"].map((label: string) => (
                    <MenuItem key={label} value={label}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              control={control}
              name="adiantamento"
            />
          </Grid>
          <Grid xs={12} lg={4}>
            <Controller
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Funcionário CLT?"
                  size="small"
                  margin="dense"
                  disabled={!isEditing}
                  fullWidth
                  variant="outlined"
                >
                  {["Sim", "Não"].map((label: string) => (
                    <MenuItem key={label} value={label}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              control={control}
              name="funcCLT"
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid xs={12} lg={6}>
            <TextField
              {...register("sindPatronal")}
              label="Sindicato Patronal"
              variant="outlined"
              fullWidth
              disabled={!isEditing}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid xs={12} lg={4}>
            <TextField
              {...register("fopagDataPGTO")}
              label="Data de pagamento FOPAG"
              variant="outlined"
              fullWidth
              disabled={!isEditing}
              type="number"
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid xs={12} lg={2}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                height: "100%",
                pt: "8px",
                pb: "8px",
              }}
            >
              <Controller
                render={({ field }) => (
                  <TypeToggleButton
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!isEditing}
                  />
                )}
                control={control}
                name="fopagDataTypePGTO"
              />
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid xs={12} lg={6}>
            <TextField
              {...register("sindClasse")}
              label="Sindicato de Classe"
              variant="outlined"
              fullWidth
              disabled={!isEditing}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid xs={12} lg={4}>
            <TextField
              {...register("fopagDataADTO")}
              label="Data de pagamento ADTO"
              variant="outlined"
              fullWidth
              disabled={!isEditing}
              type="number"
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid xs={12} lg={2}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                height: "100%",
                pt: "8px",
                pb: "8px",
              }}
            >
              <Controller
                render={({ field }) => (
                  <TypeToggleButton
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!isEditing}
                  />
                )}
                control={control}
                name="fopagDataTypeADTO"
              />
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid xs={12}>
            <TextField
              {...register("observations")}
              label="Observações"
              variant="outlined"
              fullWidth
              disabled={!isEditing}
              margin="dense"
              size="small"
              multiline
              rows={isObservationsFocused ? 10 : 5}
              onFocus={() => setIsObservationsFocused(true)}
              onBlur={() => setIsObservationsFocused(false)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  (e.target as HTMLTextAreaElement).blur();
                }
              }}
              sx={{
                "& .MuiInputBase-root": {
                  transition: "all 0.3s ease-in-out",
                },
                "& .MuiInputBase-input": {
                  transition: "all 0.3s ease-in-out",
                },
              }}
            />
          </Grid>
        </Grid>

        {/* Tarefas */}
        <Divider sx={{ my: 3 }} />
        <Box>
          <TaskHeader
            activeTasksCount={tasks.filter((t) => t.on).length}
            totalTasksCount={tasks.length}
            masterSwitch={masterSwitch}
            onMasterSwitchChange={handleMasterSwitchChange}
            isEditing={isEditing}
          />
          <TaskGrid
            tasks={tasks}
            setTasks={setTasks}
            users={users}
            isEditing={isEditing}
            progress={progress}
            taskErrors={taskErrors}
            setTaskErrors={setTaskErrors}
            folhaPagamentoStatus={getValues("info")}
            onTaskToggleAttempt={handleTaskToggleAttempt}
          />
        </Box>
      </form>

      {/* Botões flutuantes fixos */}
      {auth?.user.permission_level === "manager" && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            px: 2,
            py: 1,
            borderRadius: 2,
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            alignItems="center"
          >
            {!isEditing && (
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setIsEditing(true);
                }}
              >
                <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editar
              </Button>
            )}
            {isEditing && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={async () => {
                    setIsEditing(false);
                    setTasks(tasksBackup);
                    await loadData();
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit(onSubmit, onInvalid)}
                >
                  {!saving && "Salvar"}
                  {saving && (
                    <CircularProgress size={12} sx={{ color: "#FFF" }} />
                  )}
                </Button>
              </>
            )}
          </Stack>
        </Paper>
      )}

      <StartDateDialog
        open={startDateDialogOpen}
        onClose={handleStartDateDialogClose}
        onConfirm={handleStartDateDialogConfirm}
        tasks={tasksForDialog}
        loading={saving}
      />
    </Box>
  );
};

export default OrganizationDpTab;
